import { useNavigate, useParams } from "react-router-dom"
import { useGetOrganizationGroupQuery, useUpdateOrganizationGroupMutation } from "./organization-group-service"
import { useForm } from "react-hook-form"
import { ClientTypeDto, UpdateOrganizationGroupDto } from "@dialogics/shared/dto/types"
import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { useDeleteCallTypeMutation, useGetCallTypesByOrganizationGroupQuery } from "../call-type/service"
import { ColumnDef, Row } from "@tanstack/react-table"
import { ReactTailwindTable } from "../../component/react-tailwind-table"
import { FeatureWeightMatrix } from "../feature-weight/feature-weight-matrix"

export const OrganizationGroupEditPage = () => {
  const { id } = useParams<{ id: string }>()
  const { data: organizationGroup, isLoading } = useGetOrganizationGroupQuery(+id!)
  const [updateOrganizationGroup, { isLoading: isUpdating }] = useUpdateOrganizationGroupMutation()
  const [deleteCallType] = useDeleteCallTypeMutation()
  const { data: callTypesData } = useGetCallTypesByOrganizationGroupQuery({ organizationGroupId: +id! })

  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<"settings" | "callTypes" | "featureWeights">("settings")

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateOrganizationGroupDto>()

  useEffect(() => {
    if (organizationGroup) {
      reset({
        ...organizationGroup,
        base_facts_description: organizationGroup.base_facts_description
          ? JSON.stringify(organizationGroup.base_facts_description, null, 2)
          : "{}",
        base_score_rules: organizationGroup.base_score_rules
          ? JSON.stringify(organizationGroup.base_score_rules, null, 2)
          : "{}",
      })
    }
  }, [organizationGroup, reset])

  const validateJson = (value: string) => {
    if (!value || value.trim() === "") return true
    try {
      JSON.parse(value)
      return true
    } catch {
      return "Неверный JSON"
    }
  }

  const onSubmit = async (data: UpdateOrganizationGroupDto) => {
    const parsedData = {
      ...data,
      base_facts_description:
        !data.base_facts_description || (data.base_facts_description as string).trim() === ""
          ? null
          : JSON.parse(data.base_facts_description as string),
      base_score_rules:
        !data.base_score_rules || (data.base_score_rules as string).trim() === ""
          ? null
          : JSON.parse(data.base_score_rules as string),
    }
    try {
      await updateOrganizationGroup({ id: +id!, ...parsedData }).unwrap()
      toast.success("Конфигурация успешно сохранена")
    } catch {
      toast.error("Ошибка при сохранении конфигурации")
    }
  }

  const columns: ColumnDef<ClientTypeDto>[] = [
    {
      accessorKey: "code",
      header: "Код",
    },
    {
      accessorKey: "title",
      header: "Название",
    },
    {
      accessorKey: "description",
      header: "Описание",
    },
    {
      id: "actions",
      cell: ({ row }: { row: Row<ClientTypeDto> }) => (
        <div className={"flex items-center"}>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => navigate(`/app/admin/call-types/${row.original.id}`)}
          >
            Редактировать
          </button>
          <button className="btn btn-sm btn-error ml-2" onClick={() => deleteCallType(row.original.id)}>
            Удалить
          </button>
        </div>
      ),
    },
  ]

  if (isLoading) {
    return <div>Загрузка...</div>
  }

  if (!organizationGroup) {
    return <div>Конфигурация не найдена</div>
  }

  return (
    <div className="max-w-7xl mx-auto bg-white dark:bg-base-200 p-6 sm:p-8 rounded-lg shadow-sm">
      <h1 className="text-2xl mb-4">Редактирование конфигурации: {organizationGroup.code}</h1>

      <div role="tablist" className="tabs tabs-border">
        <button
          role="tab"
          className={`tab ${activeTab === "settings" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("settings")}
        >
          Настройки
        </button>
        <button
          role="tab"
          className={`tab ${activeTab === "callTypes" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("callTypes")}
        >
          Типы звонков
        </button>
        <button
          role="tab"
          className={`tab ${activeTab === "featureWeights" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("featureWeights")}
        >
          Веса признаков
        </button>
      </div>

      {activeTab === "settings" && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-6">
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Код</legend>
            <input
              {...register("code", { required: "Код обязателен" })}
              className={`input input-bordered w-full ${errors.code ? "input-error" : ""}`}
            />
            {errors.code && <span className="text-error text-xs">{errors.code.message}</span>}
          </fieldset>

          <div className="grid grid-cols-2 gap-4">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Описание базовых фактов</legend>
              <textarea
                {...register("base_facts_description", { validate: validateJson })}
                className={`textarea textarea-bordered w-full h-60 ${errors.base_facts_description ? "textarea-error" : ""}`}
              />
              {errors.base_facts_description && (
                <span className="text-error text-xs">{errors.base_facts_description.message as string}</span>
              )}
            </fieldset>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Базовые правила оценки</legend>
              <textarea
                {...register("base_score_rules", { validate: validateJson })}
                className={`textarea textarea-bordered w-full h-60 ${errors.base_score_rules ? "textarea-error" : ""}`}
              />
              {errors.base_score_rules && (
                <span className="text-error text-xs">{errors.base_score_rules.message as string}</span>
              )}
            </fieldset>
          </div>

          <button type="submit" className="btn btn-primary" disabled={isUpdating}>
            {isUpdating ? "Сохранение..." : "Сохранить"}
          </button>
        </form>
      )}

      {activeTab === "callTypes" && (
        <div className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/app/admin/call-types/new?organizationGroupId=${+id!}`)}
            >
              Создать тип звонка
            </button>
          </div>
          <ReactTailwindTable
            query={useGetCallTypesByOrganizationGroupQuery}
            queryParams={{ organizationGroupId: +id! }}
            columns={columns}
            pagination={{ pageIndex: 0, pageSize: 10 }}
            setPagination={() => {}}
          />
        </div>
      )}

      {activeTab === "featureWeights" && (
        <div className="mt-4">
          {callTypesData && (
            <FeatureWeightMatrix
              organizationGroupId={+id!}
              callTypes={[...callTypesData.results].sort((a, b) => a.code.localeCompare(b.code))}
            />
          )}
        </div>
      )}
    </div>
  )
}
